import { useCallback, useEffect, useMemo, useState } from 'react';
import type { OwnedArtifact } from '@shared/protocol';
import { artifactUrl } from '../lib/files';
import { ago } from './FilesPanel';
import { ArtifactViewer, Tile, useRemoveArtifact } from './ArtifactsPanel';
import { DownloadIcon, EnterIcon, TrashIcon } from './icons';

interface Props {
  /** Changes whenever the room on screen publishes or deletes; the list re-reads then. */
  refreshKey: unknown;
  /** Leaves this view for the artifact's room, with the artifact open there. */
  onOpenInRoom: (roomId: string, artifactId: string) => void;
  onError: (message: string) => void;
  /** An artifact is open in the viewer, so the settings dialog should widen for it. */
  onViewing?: (viewing: boolean) => void;
}

/**
 * Everything you own across rooms: the artifacts your own turns published.
 * A tab of the settings dialog, not tied to any one room -- opening,
 * downloading and deleting still go through the artifact's room.
 */
export function MyArtifactsPanel({ refreshKey, onOpenInRoom, onError, onViewing }: Props) {
  const [artifacts, setArtifacts] = useState<OwnedArtifact[] | undefined>();
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState('');
  const [view, setView] = useState<{ id: string; version?: number } | undefined>();
  const removeArtifact = useRemoveArtifact(onError);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/artifacts', { credentials: 'same-origin' });
      if (!r.ok) throw new Error(String(r.status));
      const body = (await r.json()) as { artifacts: OwnedArtifact[] };
      setArtifacts(body.artifacts);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!artifacts || !q) return artifacts ?? [];
    return artifacts.filter((a) =>
      [a.title, a.path, a.roomName].some((s) => s.toLowerCase().includes(q)),
    );
  }, [artifacts, filter]);

  async function remove(a: OwnedArtifact) {
    if (!(await removeArtifact(a.roomId, a, a.roomName))) return;
    if (view?.id === a.id) setView(undefined);
    setArtifacts((list) => list?.filter((x) => x.id !== a.id));
  }

  const open = view && artifacts?.find((a) => a.id === view.id);

  useEffect(() => {
    onViewing?.(!!open);
  }, [open, onViewing]);

  return (
    <>
      <div className="share__head settings__head">
        <div>
          <div className="share__title">
            My artifacts {artifacts && <span className="files__count">{artifacts.length}</span>}
          </div>
          {!open && (
            <div className="share__sub">
              What Claude published during your turns, in every room you can open. Removing one removes
              it from its room for everyone there.
            </div>
          )}
        </div>
      </div>

      {open ? (
        <ArtifactViewer
          roomId={open.roomId}
          artifact={open}
          version={view.version}
          onVersion={(version) => setView({ id: open.id, version })}
          onBack={() => setView(undefined)}
          room={{ name: open.roomName, onGo: () => onOpenInRoom(open.roomId, open.id) }}
          onDelete={() => void remove(open)}
        />
      ) : (
        <>
          {artifacts && artifacts.length > 0 && (
            <input
              className="share__filter my-artifacts__filter"
              placeholder="Filter by title, file or room…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter artifacts"
            />
          )}
          <div className="files__list">
            {failed && !artifacts && <p className="files__empty">Could not load your artifacts.</p>}
            {!failed && !artifacts && <p className="files__empty">Loading…</p>}
            {artifacts?.length === 0 && <p className="files__empty">Nothing published during your turns yet.</p>}
            {artifacts && artifacts.length > 0 && shown.length === 0 && (
              <p className="files__empty">No artifact matches “{filter.trim()}”.</p>
            )}
            {shown.map((a) => {
              const latest = a.versions.at(-1)!;
              return (
                <div className="file" key={a.id}>
                  <button className="file__main" onClick={() => setView({ id: a.id })} title={`Open ${a.title}`}>
                    <Tile roomId={a.roomId} artifact={a} />
                    <span className="file__text">
                      <span className="file__name-row">
                        <span className="file__name">{a.title}</span>
                      </span>
                      <span className="file__meta">
                        {a.versions.length > 1 && <span className="artifact-version">v{latest.version}</span>}
                        {a.roomName} · {a.path} · {ago(a.updatedAt)}
                      </span>
                    </span>
                  </button>
                  <button
                    className="file__action"
                    onClick={() => onOpenInRoom(a.roomId, a.id)}
                    aria-label={`Open ${a.title} in ${a.roomName}`}
                    title={`Open in “${a.roomName}”`}
                  >
                    <EnterIcon />
                  </button>
                  <a
                    className="file__action"
                    href={artifactUrl(a.roomId, a.id, latest.version, false)}
                    download
                    aria-label={`Download ${a.title}`}
                    title="Download"
                  >
                    <DownloadIcon />
                  </a>
                  <button
                    className="file__action file__action--danger"
                    onClick={() => void remove(a)}
                    aria-label={`Delete ${a.title}`}
                    title="Remove from its room"
                  >
                    <TrashIcon />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
